/**
 * Production-Safe Feature Flags
 * 
 * SAFETY: These flags allow instant rollback without redeployment.
 * 
 * All flags default to TRUE (enabled) in production.
 * Set to false in .env to disable specific fixes during rollback scenarios.
 * 
 * Usage:
 *   import { FEATURE_FLAGS } from '@/config/featureFlags';
 *   if (FEATURE_FLAGS.ENABLE_SAFE_AUTH_FLOW) { ... }
 */

// Environment variable reader with safe defaults
const getFlag = (envVar, defaultValue = true) => {
  const value = import.meta.env[envVar];
  // If not set, use default (true = enabled)
  if (value === undefined || value === '') return defaultValue;
  // Explicitly check for 'false' string
  return value !== 'false';
};

/**
 * Feature Flags for Production Safety
 * 
 * ENABLE_SAFE_AUTH_FLOW:
 *   - Auth readiness checks before popup
 *   - Centralized auth logging
 *   - Race condition prevention
 * 
 * ENABLE_IDEMPOTENT_WRITES:
 *   - Client-side deduplication
 *   - Write locks during submission
 *   - Double-click prevention
 */
export const FEATURE_FLAGS = Object.freeze({
  /**
   * Safe Auth Flow
   * Guards against:
   * - Auth popup before Firebase ready
   * - Race conditions during auth
   * - Double popup invocation
   */
  ENABLE_SAFE_AUTH_FLOW: getFlag('VITE_ENABLE_SAFE_AUTH_FLOW', true),
  
  /**
   * Idempotent Writes
   * Guards against:
   * - Double item creation
   * - Network retry duplicates
   * - StrictMode double-invoke
   * - Rapid button clicks
   */
  ENABLE_IDEMPOTENT_WRITES: getFlag('VITE_ENABLE_IDEMPOTENT_WRITES', true),
});

/**
 * Production-safe logging for feature flag usage
 * Non-PII, minimal footprint
 */
export const logFeatureFlagUsage = (flagName, isEnabled) => {
  // Only log in development or if explicitly enabled
  if (import.meta.env.DEV) {
    console.log(`[FeatureFlag] ${flagName}: ${isEnabled ? 'ENABLED' : 'DISABLED'}`);
  }
};

export default FEATURE_FLAGS;
